import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Calendar, Search, Phone, MapPin, User, X, CheckCircle, Clock, Play, XCircle, CalendarDays } from 'lucide-react';

const today = () => new Date().toISOString().split('T')[0];

const Pickups = () => {
  const [pickups, setPickups] = useState([]);
  const [remitentes, setRemitentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [remitenteSearch, setRemitenteSearch] = useState('');
  const [showRemitenteDropdown, setShowRemitenteDropdown] = useState(false);
  const [selectedRemitente, setSelectedRemitente] = useState(null);
  const [filterFecha, setFilterFecha] = useState(today());
  const [filterEstado, setFilterEstado] = useState('');
  const [formData, setFormData] = useState({
    fecha_programada: today(),
    hora_inicio: '09:00',
    hora_fin: '10:00',
    direccion: '',
    nombre_cliente: '',
    telefono_cliente: '',
    articulos_lista: '',
    notas: ''
  });

  useEffect(() => {
    fetchRemitentes();
  }, []);

  useEffect(() => {
    fetchPickups();
  }, [filterFecha, filterEstado]);

  const fetchPickups = async () => {
    try {
      const params = {};
      if (filterFecha) params.fecha = filterFecha;
      if (filterEstado) params.estado = filterEstado;
      const response = await api.get('/pickups', { params });
      setPickups(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar recogidas');
    } finally {
      setLoading(false);
    }
  };

  const fetchRemitentes = async () => {
    try {
      const response = await api.get('/customers', { params: { tipo: 'remitente', limit: 200 } });
      setRemitentes(response.data.data || []);
    } catch (error) {
      console.error('Error loading remitentes');
    }
  };

  const handleSelectRemitente = (remitente) => {
    setSelectedRemitente(remitente);
    setRemitenteSearch(remitente.nombre_completo);
    setShowRemitenteDropdown(false);
    setFormData({
      ...formData,
      nombre_cliente: remitente.nombre_completo,
      telefono_cliente: remitente.telefono || '',
      direccion: [remitente.direccion_completa, remitente.ciudad, remitente.estado].filter(Boolean).join(', ')
    });
  };

  const clearRemitente = () => {
    setSelectedRemitente(null);
    setRemitenteSearch('');
    setFormData({ ...formData, nombre_cliente: '', telefono_cliente: '', direccion: '' });
  };

  const filteredRemitentes = remitentes.filter(r =>
    r.nombre_completo?.toLowerCase().includes(remitenteSearch.toLowerCase()) ||
    r.telefono?.includes(remitenteSearch) ||
    r.cedula_pasaporte?.includes(remitenteSearch)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/pickups', formData);
      toast.success('Recogida programada');
      fetchPickups();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Error al programar recogida');
    }
  };

  const changeStatus = async (id, newEstado) => {
    try {
      await api.put(`/pickups/${id}/estado`, { estado: newEstado });
      toast.success(`Estado cambiado a ${newEstado}`);
      fetchPickups();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const resetForm = () => {
    setFormData({
      fecha_programada: today(),
      hora_inicio: '09:00',
      hora_fin: '10:00',
      direccion: '',
      nombre_cliente: '',
      telefono_cliente: '',
      articulos_lista: '',
      notas: ''
    });
    setSelectedRemitente(null);
    setRemitenteSearch('');
  };

  // Counts
  const countByEstado = (estado) => pickups.filter(p => (p.estado || 'programada') === estado).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 p-3 sm:p-6 pb-24">
      <div className="max-w-7xl mx-auto space-y-4">

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 text-blue-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-blue-800">{countByEstado('programada')}</p>
              <p className="text-sm font-semibold text-blue-600">Programadas</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-yellow-200 bg-yellow-50">
            <CardContent className="p-4 text-center">
              <Play className="h-6 w-6 text-yellow-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-yellow-800">{countByEstado('en_progreso')}</p>
              <p className="text-sm font-semibold text-yellow-600">En Progreso</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-800">{countByEstado('completada')}</p>
              <p className="text-sm font-semibold text-green-600">Completadas</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-gray-200 bg-gray-50">
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 text-gray-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-gray-800">{pickups.length}</p>
              <p className="text-sm font-semibold text-gray-600">Total Hoy</p>
            </CardContent>
          </Card>
        </div>

        <Card className="shadow-xl border-2 border-orange-100">
          <CardHeader className="bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-t-lg">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Calendar className="h-8 w-8" />
                </div>
                <div>
                  <CardTitle className="text-2xl md:text-3xl font-bold">Recogidas del Dia</CardTitle>
                  <p className="text-orange-100 text-base">
                    {filterFecha === today() ? 'Hoy' : filterFecha} - {pickups.length} recogidas
                  </p>
                </div>
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button data-testid="new-pickup-btn" className="h-12 px-6 bg-white text-orange-600 hover:bg-orange-50 shadow-lg font-bold" size="lg">
                    <Plus className="mr-2 h-5 w-5" /> Nueva Recogida
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="pickup-dialog-desc">
                  <DialogHeader>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                      <Calendar className="h-6 w-6 text-orange-500" />
                      Programar Recogida
                    </DialogTitle>
                    <p id="pickup-dialog-desc" className="text-sm text-gray-500">Busque un remitente o ingrese los datos manualmente</p>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Remitente Search */}
                    <div>
                      <Label className="text-base font-semibold flex items-center gap-2">
                        <User className="h-4 w-4" /> Buscar Remitente Guardado
                      </Label>
                      <div className="relative mt-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <Input
                          data-testid="pickup-remitente-search"
                          placeholder="Buscar por nombre, telefono o cedula..."
                          value={remitenteSearch}
                          onChange={(e) => {
                            setRemitenteSearch(e.target.value);
                            setShowRemitenteDropdown(true);
                            if (!e.target.value) clearRemitente();
                          }}
                          onFocus={() => setShowRemitenteDropdown(true)}
                          className="pl-10 h-12 text-lg"
                        />
                        {selectedRemitente && (
                          <Button type="button" variant="ghost" size="icon" className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8" onClick={clearRemitente}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                        {showRemitenteDropdown && remitenteSearch && !selectedRemitente && (
                          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-blue-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                            {filteredRemitentes.length === 0 ? (
                              <div className="p-3 text-gray-500 text-center">No se encontraron remitentes</div>
                            ) : (
                              filteredRemitentes.slice(0, 8).map((r) => (
                                <button
                                  key={r.id}
                                  type="button"
                                  data-testid={`pickup-remitente-option-${r.id}`}
                                  className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b last:border-b-0 transition-colors"
                                  onClick={() => handleSelectRemitente(r)}
                                >
                                  <div className="font-bold text-base">{r.nombre_completo}</div>
                                  <div className="text-sm text-gray-500 flex items-center gap-3">
                                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{r.telefono}</span>
                                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{r.ciudad || r.direccion_completa}</span>
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      {selectedRemitente && (
                        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-700 font-semibold">Remitente seleccionado - datos cargados automaticamente</p>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-base font-semibold">Fecha *</Label>
                        <Input data-testid="pickup-date" type="date" value={formData.fecha_programada} onChange={(e) => setFormData({...formData, fecha_programada: e.target.value})} required className="h-12 mt-1 text-lg" />
                      </div>
                      <div>
                        <Label className="text-base font-semibold">Hora *</Label>
                        <Input data-testid="pickup-time" type="time" value={formData.hora_inicio} onChange={(e) => setFormData({...formData, hora_inicio: e.target.value})} required className="h-12 mt-1 text-lg" />
                      </div>
                    </div>

                    <div>
                      <Label className="text-base font-semibold flex items-center gap-2"><User className="h-4 w-4" /> Nombre Cliente *</Label>
                      <Input data-testid="pickup-client-name" value={formData.nombre_cliente} onChange={(e) => setFormData({...formData, nombre_cliente: e.target.value})} required className="h-12 mt-1 text-lg" />
                    </div>

                    <div>
                      <Label className="text-base font-semibold flex items-center gap-2"><Phone className="h-4 w-4" /> Telefono *</Label>
                      <Input data-testid="pickup-client-phone" value={formData.telefono_cliente} onChange={(e) => setFormData({...formData, telefono_cliente: e.target.value})} required className="h-12 mt-1 text-lg" />
                    </div>

                    <div>
                      <Label className="text-base font-semibold flex items-center gap-2"><MapPin className="h-4 w-4" /> Direccion *</Label>
                      <Input data-testid="pickup-address" value={formData.direccion} onChange={(e) => setFormData({...formData, direccion: e.target.value})} required className="h-12 mt-1 text-lg" />
                    </div>

                    <div>
                      <Label className="text-base font-semibold">Articulos</Label>
                      <Input data-testid="pickup-items" value={formData.articulos_lista} onChange={(e) => setFormData({...formData, articulos_lista: e.target.value})} placeholder="3 cajas, 2 tanques..." className="h-12 mt-1 text-lg" />
                    </div>

                    <div>
                      <Label className="text-base font-semibold">Notas</Label>
                      <Input data-testid="pickup-notes" value={formData.notas || ''} onChange={(e) => setFormData({...formData, notas: e.target.value})} placeholder="Notas adicionales..." className="h-12 mt-1 text-lg" />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <Button type="button" variant="outline" size="lg" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button data-testid="submit-pickup-btn" type="submit" size="lg" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 px-8">
                        <Calendar className="mr-2 h-5 w-5" /> Programar
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-gray-500" />
                <Input
                  data-testid="filter-pickup-date"
                  type="date"
                  value={filterFecha}
                  onChange={(e) => setFilterFecha(e.target.value)}
                  className="h-11 w-44"
                />
                {filterFecha !== today() && (
                  <Button variant="ghost" size="sm" onClick={() => setFilterFecha(today())} className="text-orange-600 font-bold">
                    Hoy
                  </Button>
                )}
              </div>
              <Select value={filterEstado || 'todos'} onValueChange={(v) => setFilterEstado(v === 'todos' ? '' : v)}>
                <SelectTrigger data-testid="filter-pickup-estado" className="h-11 w-44">
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="programada">Programada</SelectItem>
                  <SelectItem value="en_progreso">En Progreso</SelectItem>
                  <SelectItem value="completada">Completada</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Cargando recogidas...</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border-2 border-gray-100 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-orange-50 to-amber-50">
                      <TableHead className="font-bold text-gray-900 text-base">Hora</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Cliente</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Telefono</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Direccion</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Articulos</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Estado</TableHead>
                      <TableHead className="font-bold text-gray-900 text-base">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pickups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-12">
                          <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500 text-lg">No hay recogidas para esta fecha</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pickups.map((pickup) => {
                        const estado = pickup.estado || 'programada';
                        return (
                          <TableRow key={pickup.id} className={`hover:bg-orange-50/50 transition-colors ${estado === 'completada' ? 'bg-green-50/30' : estado === 'en_progreso' ? 'bg-yellow-50/30' : ''}`}>
                            <TableCell className="font-medium text-base">{pickup.hora_inicio}</TableCell>
                            <TableCell className="font-bold text-base">{pickup.nombre_cliente}</TableCell>
                            <TableCell className="text-base">
                              <span className="flex items-center gap-1"><Phone className="h-4 w-4 text-gray-400" />{pickup.telefono_cliente}</span>
                            </TableCell>
                            <TableCell className="text-base max-w-[200px] truncate">{pickup.direccion}</TableCell>
                            <TableCell className="text-base">{pickup.articulos_lista || '-'}</TableCell>
                            <TableCell>
                              <StatusBadge estado={estado} />
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 flex-wrap">
                                {estado === 'programada' && (
                                  <Button
                                    data-testid={`pickup-start-${pickup.id}`}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => changeStatus(pickup.id, 'en_progreso')}
                                    className="h-9 border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                                    title="En Progreso"
                                  >
                                    <Play className="h-4 w-4 mr-1" /> Iniciar
                                  </Button>
                                )}
                                {(estado === 'programada' || estado === 'en_progreso') && (
                                  <Button
                                    data-testid={`pickup-complete-${pickup.id}`}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => changeStatus(pickup.id, 'completada')}
                                    className="h-9 border-green-400 text-green-700 hover:bg-green-50"
                                    title="Completada"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" /> Completar
                                  </Button>
                                )}
                                {estado !== 'cancelada' && estado !== 'completada' && (
                                  <Button
                                    data-testid={`pickup-cancel-${pickup.id}`}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => changeStatus(pickup.id, 'cancelada')}
                                    className="h-9 border-red-300 text-red-600 hover:bg-red-50"
                                    title="Cancelar"
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                )}
                                {estado === 'completada' && (
                                  <span className="text-green-600 font-bold text-sm flex items-center gap-1">
                                    <CheckCircle className="h-4 w-4" /> Listo
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const StatusBadge = ({ estado }) => {
  const config = {
    programada: { bg: 'bg-blue-100 text-blue-800 border-blue-300', icon: <Clock className="h-3 w-3" />, label: 'Programada' },
    en_progreso: { bg: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: <Play className="h-3 w-3" />, label: 'En Progreso' },
    completada: { bg: 'bg-green-100 text-green-800 border-green-300', icon: <CheckCircle className="h-3 w-3" />, label: 'Completada' },
    cancelada: { bg: 'bg-red-100 text-red-800 border-red-300', icon: <XCircle className="h-3 w-3" />, label: 'Cancelada' }
  };
  const c = config[estado] || config.programada;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${c.bg}`}>
      {c.icon} {c.label}
    </span>
  );
};

export default Pickups;
